import { Component } from '@theme/component';

/** Navigation enhancement; native details and links remain usable without JavaScript. */
class AdminV2Navigation extends Component {
  #controller;
  get #drawer() {
    return this.querySelector('.admin-v2-menu__drawer');
  }
  #media = matchMedia('(max-width: 1023px)');
  #onResize = () => this.#closeAll();

  connectedCallback() {
    super.connectedCallback();
    this.#controller?.abort();
    this.#controller = new AbortController();
    const options = { signal: this.#controller.signal };
    this.addEventListener('toggle', this.#onToggle, { ...options, capture: true });
    this.addEventListener('click', this.#onClick, { ...options, capture: true });
    this.addEventListener('keydown', this.#onKeydown, options);
    document.addEventListener('click', this.#onOutside, options);
    this.#media.addEventListener('change', this.#onResize, options);
  }

  disconnectedCallback() {
    this.#controller?.abort();
    if (this.#drawer?.open) document.documentElement.classList.remove('admin-v2-menu-open');
    super.disconnectedCallback();
  }

  #onToggle = (event) => {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement)) return;
    if (details === this.#drawer) {
      document.documentElement.classList.toggle('admin-v2-menu-open', details.open);
      const body = this.querySelector('.admin-v2-menu__drawer-body');
      if (details.open) {
        this.#syncServiceLinks();
        body?.setAttribute('role', 'dialog');
        body?.setAttribute('aria-modal', 'true');
        this.querySelector('[data-menu-close] button, button[data-menu-close]')?.focus();
      } else {
        body?.removeAttribute('role');
        body?.removeAttribute('aria-modal');
        this.querySelectorAll('.admin-v2-menu__item[open]').forEach(item => { item.open = false; });
      }
      this.#syncHeading();
      return;
    }
    if (!details.matches('.admin-v2-menu__item')) return;
    if (details.open) {
      this.querySelectorAll('.admin-v2-menu__item[open]').forEach(item => { if (item !== details) item.open = false; });

    }
    this.#syncHeading();
    if (details.open && this.#drawer) this.querySelector('.admin-v2-menu__header-back')?.focus();
  };

  #syncHeading() {
    const item = this.querySelector('.admin-v2-menu__item[open]');
    this.classList.toggle('has-panel', !!item);
    const title = this.querySelector('[data-menu-title]');
    if (title) title.textContent = item?.querySelector('summary')?.textContent.trim() || title.dataset.rootTitle;
  }

  #syncServiceLinks() {
    const target = this.querySelector('.admin-v2-menu__service nav');
    const source = document.querySelector('.admin-v2-footer__links');
    if (!target || !source) return;
    const links = [...source.children].map(item => item.cloneNode(true));
    const withdrawal = document.querySelector('.admin-v2-footer__withdrawal');
    if (withdrawal) links.push(withdrawal.cloneNode(true));
    target.replaceChildren(...links);
  }

  #closeAll() {
    this.querySelectorAll('details[open]').forEach(item => { item.open = false; });
    this.#syncHeading();
    if (this.#drawer) document.documentElement.classList.remove('admin-v2-menu-open');
  }

  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('[data-menu-search]')) {
      this.#closeAll();
      this.#drawer?.querySelector('summary')?.focus();
    }
    if (event.target.closest('.admin-v2-menu__service admin-v2-cookie-preferences button')) this.#closeAll();
    if (event.target.closest('[data-menu-close]')) {
      this.#closeAll();
      this.#drawer?.querySelector('summary')?.focus();
    }
    if (event.target.closest('[data-menu-back]')) {
      const item = this.querySelector('.admin-v2-menu__item[open]');
      if (item) { item.open = false; this.#syncHeading(); item.querySelector('summary')?.focus(); }
    }
  };

  #onOutside = (event) => {
    if (!this.#drawer && event.target instanceof Node && !this.contains(event.target)) this.#closeAll();
  };

  #onKeydown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      const item = this.querySelector('.admin-v2-menu__item[open]');
      if (item) { item.open = false; this.#syncHeading(); item.querySelector('summary')?.focus(); }
      else if (this.#drawer?.open) { this.#closeAll(); this.#drawer.querySelector('summary')?.focus(); }
    }
    if (event.key === 'ArrowDown' && event.target instanceof Element && event.target.matches('.admin-v2-menu__item > summary')) {
      event.preventDefault();
      event.target.parentElement.open = true;
      event.target.parentElement.querySelector('a')?.focus();
    }
    if (event.key === 'Tab' && this.#drawer?.open) {
      const body = this.querySelector('.admin-v2-menu__drawer-body');
      const focusable = [...body.querySelectorAll('a,button,summary')].filter(item => item.getClientRects().length);
      const first = focusable[0], last = focusable.at(-1);
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    }
  };
}
if (!customElements.get('admin-v2-navigation')) customElements.define('admin-v2-navigation', AdminV2Navigation);
